import React, { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { PHOTO_CATEGORY_LABELS, regionMapping } from 'alakai-aloha-shared'
import { azimuthToCompassDirection } from '../../lib/photoUtils'
import AppLink from '../shared/AppLink'

function formatCategories(spot) {
  if (!Array.isArray(spot.photoCategories)) return ''
  return spot.photoCategories.map((c) => PHOTO_CATEGORY_LABELS[c] || c).join(', ')
}

function getRegion(spot) {
  return spot.region || regionMapping[spot.area] || 'Other'
}

export default function PhotoSpotPreviewModal({ spot, onClose }) {
  if (!spot) return null

  return (
    <Transition appear show={!!spot} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-[9999] overflow-y-auto" onClose={onClose}>
        <div className="flex min-h-screen items-center justify-center px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-50"
            leave="ease-in duration-200"
            leaveFrom="opacity-50"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} aria-hidden="true" />
          </Transition.Child>

          <span className="inline-block h-screen align-middle" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="relative inline-block w-full max-w-3xl p-6 my-8 overflow-auto text-left align-middle bg-[#1F2A36] text-white rounded-lg shadow-xl z-[9999] pointer-events-auto">
              <button
                type="button"
                className="absolute top-3 right-3 text-white hover:text-gray-300 rounded-full p-1 bg-black bg-opacity-30 hover:bg-opacity-60 transition"
                onClick={onClose}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeWidth={2} d="M6 6l12 12M18 6l-12 12" />
                </svg>
              </button>

              <Dialog.Title as="h3" className="text-2xl font-semibold mb-4">
                {spot.name}
              </Dialog.Title>

              {spot.images?.[0]?.asset?.url ? (
                <div className="w-full h-[320px] sm:h-[420px] bg-black/60 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                  <img
                    src={spot.images[0].asset.url}
                    alt={spot.images?.[0]?.alt || spot.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              ) : null}

              {spot.shortDescription ? (
                <p className="text-sm text-white/90 mb-4">{spot.shortDescription}</p>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-white/80">
                <div>
                  <strong className="text-white">Region:</strong> {getRegion(spot)}
                </div>
                {spot.area ? (
                  <div>
                    <strong className="text-white">Area:</strong> {spot.area}
                  </div>
                ) : null}
                {typeof spot.primaryViewAzimuth === 'number' ? (
                  <div>
                    <strong className="text-white">View direction:</strong>{' '}
                    {azimuthToCompassDirection(spot.primaryViewAzimuth)} ({spot.primaryViewAzimuth}°)
                  </div>
                ) : null}
                {formatCategories(spot) ? (
                  <div>
                    <strong className="text-white">Categories:</strong> {formatCategories(spot)}
                  </div>
                ) : null}
              </div>

              {spot.slug ? (
                <div className="mt-4">
                  <AppLink
                    to={`/photo-planner/${spot.slug}`}
                    className="text-emerald-300 hover:text-emerald-200 font-semibold"
                  >
                    How to get this shot →
                  </AppLink>
                </div>
              ) : null}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
